




















import axios from 'axios';
import io from 'socket.io-client';
import VueSocketIO from 'vue-socket.io';
import { Vue, Component } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import SettingsService from '@/services/crm/settings.service';
import SettingsModel from '@/models/crm/settings.model';

type JsonObj = Record<string, unknown>;

const lsKeysParseJson = [
  'sessionService',
  'activeClient',
  'userContactInfo',
  'settings',
  'contactsList',
  'createConversationPayload',
] as const;

const lsKeysPlain = [
  'clientType',
  'contactNumber',
  'apiUrl',
  'socketIOUrl',
  'authToken',
  'token',
] as const;

function safeJsonParse<T extends JsonObj = JsonObj>(raw: string | null): T | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as T; } catch { return null; }
}

function toTruthy(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y';
  }
  return false;
}

@Component({
  components: {
    CrmAppBar: () => import('@/components/crm/app-bar.vue'),
  },
})
export default class GeoChatView extends Vue {
  @inject(InjectionIdEnum.CrmSettingsService)
  private settingsService!: SettingsService;

  chatContext: Record<string, unknown> = {};

  contextReady = false;

  private settings: SettingsModel | null = null;

  created(): void {
    document.title = 'GeoChat';
    this.loadSettings();
    this.composeChatContext();
    this.setupAxios();
    this.setupAuthToken();
    this.setupSocketIO();
    this.contextReady = true;
    this.$nextTick(() => this.cleanupUrlAndStorage());
  }

  // ================= CORE =================
  private composeChatContext(): void {
    const q = (this.$route?.query ?? {}) as Record<string, unknown>;
    const p = (this.$route?.params ?? {}) as Record<string, unknown>;

    const tokenFromUrl = q.token ? String(q.token).trim() : '';
    const convIdFromUrl = q.conversationId ? String(q.conversationId).trim() : '';
    const clientIdFromUrl = q.clientId ? String(q.clientId).trim() : '';
    const createFlag = toTruthy(q.create);

    const apiUrlFromUrl = q.apiUrl ? String(q.apiUrl) : '';
    const socketUrlFromUrl = q.socketIOUrl ? String(q.socketIOUrl) : '';
    // Quem abre de outra origem (funil no GEOvendas360) nao alcanca o localStorage daqui:
    // o numero do contato vem na URL e entra no store como se viesse do painel.
    const contactNumberFromUrl = q.contactNumber ? String(q.contactNumber).trim() : '';
    if (contactNumberFromUrl) {
      this.$store.commit('setContactNumber', contactNumberFromUrl);
    }

    const ctx: Record<string, unknown> = {};
    lsKeysParseJson.forEach((k) => { ctx[k] = safeJsonParse(localStorage.getItem(k)); });
    lsKeysPlain.forEach((k) => {
      const raw = localStorage.getItem(k);
      if (k === 'authToken' || k === 'token') {
        if (!ctx.tokenFromLS) ctx.tokenFromLS = raw || null;
      } else {
        (ctx as Record<string, unknown>)[k] = raw || null;
      }
    });

    const tokenFromLS = (ctx.tokenFromLS as string | null) || '';
    const finalToken = (tokenFromUrl || tokenFromLS).trim();

    const ss = (ctx.sessionService || null) as (Record<string, unknown> | null);

    const apiUrl = apiUrlFromUrl
      || (ctx.apiUrl as string | null)
      || (ss && (ss.apiUrl as string))
      || null;

    const socketIOUrl = socketUrlFromUrl
      || (ctx.socketIOUrl as string | null)
      || (ss && (ss.socketIOUrl as string))
      || null;

    if (apiUrlFromUrl) localStorage.setItem('apiUrl', apiUrlFromUrl);
    if (socketUrlFromUrl) localStorage.setItem('socketIOUrl', socketUrlFromUrl);

    this.chatContext = {
      ...p,
      ...q,
      token: finalToken || null,
      conversationId: convIdFromUrl || (p as Record<string, unknown>).conversationId || null,
      clientId: clientIdFromUrl || (p as Record<string, unknown>).clientId || null,
      create: createFlag,
      sessionService: ss,
      activeClient: ctx.activeClient || null,
      userContactInfo: ctx.userContactInfo || null,
      clientType: ctx.clientType || null,
      settings: ctx.settings || null,
      contactsList: ctx.contactsList || null,
      contactNumber: ctx.contactNumber || null,
      createConversationPayload: ctx.createConversationPayload || null,
      apiUrl,
      socketIOUrl,
    };
  }

  private setupAxios(): void {
    const baseURL = (this.chatContext.apiUrl as string | null) || null;
    if (baseURL) axios.defaults.baseURL = baseURL;
  }

  private setupAuthToken(): void {
    const token = (this.chatContext.token as string | null) || '';
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      localStorage.setItem('authToken', token);
    }
  }

  private setupSocketIO(): void {
    try {
      const socketURL = (this.chatContext.socketIOUrl as string | null)
        || (
          (
            this.chatContext.sessionService as Record<string, unknown> | null
          )?.socketIOUrl as string | undefined
        )
        || null;

      if (!socketURL) return;

      const flag = (Vue as unknown as { geochatSocketReady?: boolean });
      if (flag.geochatSocketReady === true) return;

      const debug = socketURL.includes('localhost') || socketURL.includes('127.0.0.1');

      const socketInstance = io(socketURL, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 9999,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
      });

      Vue.use(new VueSocketIO({ debug, connection: socketInstance }));
      flag.geochatSocketReady = true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      (this as unknown as { $notify?: { error?: (m: string) => void } }).$notify?.error?.(msg);
    }
  }

  private cleanedOnce = false;

  private cleanupUrlAndStorage(): void {
    if (this.cleanedOnce) return;

    const currentQuery = (this.$route?.query
      ? { ...this.$route.query }
      : {}) as Record<string, string | (string | null)[] | null | undefined>;

    let changed = false;
    const keysToRemove = [
      'token', 'conversationId', 'create', 'clientId', 'apiUrl', 'socketIOUrl', 'contactNumber',
    ] as const;

    keysToRemove.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(currentQuery, k)) {
        delete currentQuery[k];
        changed = true;
      }
    });

    if (changed) {
      this.$router
        .replace({
          path: this.$route.path,
          params: this.$route.params,
          query: currentQuery,
        })
        .catch(() => {});
    }

    // limpa apenas LS transitório (mantém sessionService/apiUrl/socketIOUrl)
    const lsEphemeralKeys = [
      'activeClient',
      'userContactInfo',
      'clientType',
      'settings',
      'contactsList',
      'contactNumber',
      'createConversationPayload',
      'openConversationId',
    ] as const;
    (lsEphemeralKeys as readonly string[]).forEach((k) => localStorage.removeItem(k));

    this.cleanedOnce = true;
  }

  onOpenOverview(): void {
    this.$router.push({
      name: 'GeoChatOverview',
      query: { ...this.$route.query }, // Mantém todos os parâmetros atuais
    });
  }

  async loadSettings(): Promise<void> {
    if (!this.settings) {
      try {
        this.settings = await this.settingsService.getSettings();
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
        this.settings = null;
      }
    }
  }

  get showOverview(): boolean {
    return !(this.settings?.numeroCanalChatVendas != null && this.settings?.usaChatVendas);
  }
}
